<script setup lang="ts">
interface Props {
  variant?: 'primary' | 'contrast' | 'outline-primary' | 'outline-contrast' | 'ghost' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  type: 'button',
  fullWidth: false,
});

defineEmits<{
  click: [event: MouseEvent];
}>();
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="[
      'inline-flex items-center justify-center font-medium transition-all duration-200 rounded-ui-button focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ui-neutral-950',
      {
        'bg-ui-accent-400 text-ui-neutral-950 hover:bg-ui-accent-300 focus:ring-ui-accent-400 shadow-lg shadow-ui-accent-400/20': variant === 'primary',
        'bg-ui-fg-100 text-ui-neutral-950 hover:bg-ui-fg-200 focus:ring-ui-fg-100': variant === 'contrast',
        'bg-transparent border-2 border-ui-accent-400 text-ui-accent-400 hover:bg-ui-accent-400/10 focus:ring-ui-accent-400': variant === 'outline-primary',
        'bg-transparent border-2 border-ui-fg-100 text-ui-fg-100 hover:bg-ui-fg-100/10 focus:ring-ui-fg-100': variant === 'outline-contrast',
        // Ghost (subtle)
        'bg-transparent text-ui-fg-100 hover:bg-ui-light/5 focus:ring-ui-light/20': variant === 'ghost',
        // Secondary (dark)
        'bg-ui-neutral-800 text-ui-fg-100 border border-ui-neutral-600 hover:bg-ui-neutral-700 focus:ring-ui-neutral-600': variant === 'secondary',
        // Danger
        'bg-ui-critical-800 text-ui-light hover:bg-ui-critical-700 focus:ring-ui-critical-800': variant === 'danger',
      },
      {
        'px-3 py-1.5 text-xs font-sans tracking-wide': size === 'sm',
        'px-4 py-2.5 text-sm font-sans': size === 'md',
        'px-6 py-3 text-base font-sans': size === 'lg',
      },
      {
        'opacity-50 cursor-not-allowed': disabled || loading,
        'w-full': fullWidth,
        'hover:scale-[1.02] active:scale-[0.98]': !disabled && !loading,
      },
    ]"
    @click="$emit('click', $event)"
  >
    <svg
      v-if="loading"
      class="animate-spin -ml-1 mr-2 h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
    <slot />
  </button>
</template>
